import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';
import { useEscapeKey, useFocusTrap, usePresence } from '@/hooks';
import { cn } from '@/lib/cn';
import { formatFileSize } from '@/lib/format';
import { RENDERABLE_TYPES } from '@/lib/previewable';

const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4] as const;
const DEFAULT_ZOOM_INDEX = 2;

export interface DocumentViewerProps {
  open: boolean;
  onClose: () => void;
  /** Shown in the title bar - typically the document's original filename. */
  title: string;
  /** The type as recorded by the API. Anything unrenderable shows a prompt. */
  mimeType: string;
  sizeBytes?: number;
  /**
   * Fetches the bytes, once per document. The signal is aborted when the
   * viewer closes, so shutting a large file mid-download stops the transfer
   * rather than leaving it to finish into a blob nobody will look at.
   */
  load: (signal: AbortSignal) => Promise<Blob>;
  /** Offered alongside the preview, and instead of it for unrenderable types. */
  onDownload?: () => void;
  /**
   * Facts about the thing in the picture, shown in a strip under the title.
   *
   * A scan is rarely opened to look at it - it is opened to check something
   * written on it, and the record already knows what that says. Putting the
   * number and the dates here means nobody has to hold them in their head
   * while they squint at a photocopy.
   */
  details?: ViewerDetail[];
}

export interface ViewerDetail {
  label: string;
  value: string;
  /** Draws attention to a date that has passed, or is about to. */
  tone?: 'default' | 'warning' | 'danger';
}

/**
 * Full-screen viewer for PDFs and images held against a tenant.
 *
 * Documents never become a URL the browser navigates to: the bytes are fetched
 * with the session cookie, re-typed from {@link RENDERABLE_TYPES} and shown
 * from a blob URL that is revoked the moment the viewer closes. Nothing is
 * left behind for a stale tab or a shared link to reach.
 */
export function DocumentViewer({ open, ...props }: DocumentViewerProps) {
  if (!open) {
    return null;
  }

  /* Keyed on the document so opening a different one remounts rather than
     resetting state by hand - the zoom, the blob and the error all start
     fresh because the component itself is new. */
  return <ViewerSurface key={`${props.mimeType}:${props.title}`} {...props} />;
}

type Loaded = { status: 'ready'; url: string } | { status: 'failed'; message: string };

function ViewerSurface({
  onClose,
  title,
  mimeType,
  sizeBytes,
  load,
  onDownload,
  details = [],
}: Omit<DocumentViewerProps, 'open'>) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { entered } = usePresence(true);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  /* Fit is the default: a scan or a photo is usually far larger than the
     window, and opening it at 100% would show the top-left corner of it. */
  const [fit, setFit] = useState(true);
  /* Zoom has to resize the element rather than transform it, so the scroll
     container gets a real box to scroll - hence the image's own dimensions. */
  const [naturalWidth, setNaturalWidth] = useState(0);

  const kind = RENDERABLE_TYPES[mimeType];

  useEscapeKey(onClose, true);
  useFocusTrap(panelRef, true);

  /* Captured once. This component is keyed per document, so the loader it
     mounted with is always the right one - which keeps the fetch out of the
     render cycle even though callers pass an inline closure. */
  const loadRef = useRef(load);

  useEffect(() => {
    if (kind === undefined) {
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;

    void loadRef
      .current(controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) {
          return;
        }

        /* Re-typed from our own table, never from `blob.type`. */
        objectUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
        setLoaded({ status: 'ready', url: objectUrl });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        setLoaded({
          status: 'failed',
          message: cause instanceof Error ? cause.message : 'The file could not be opened.',
        });
      });

    return () => {
      controller.abort();

      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [kind, mimeType]);

  /* The page behind must not scroll while the viewer covers it. */
  useEffect(() => {
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, []);

  const zoomBy = useCallback((step: number) => {
    setFit(false);
    setZoomIndex((index) => Math.min(ZOOM_STEPS.length - 1, Math.max(0, index + step)));
  }, []);

  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;
  const showZoom = kind === 'image' && loaded?.status === 'ready';

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col bg-black/75',
        /* Only an entrance: the surface unmounts on close, and a document
           that lingers while fading is worse than one that is simply gone. */
        'transition-opacity duration-quick ease-enter',
        entered ? 'opacity-100' : 'opacity-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${title}`}
    >
      <div ref={panelRef} className="flex h-full flex-col">
        {/* The name takes the whole first line on a phone and the controls the
            second. Sharing one line truncated the filename to six characters,
            which is the one thing the header exists to say. */}
        <header className="chrome-bar flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2">
          <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1">
            <Icon name="document" size={15} className="shrink-0 text-content-muted" />

            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-content" title={title}>
                {title}
              </p>
              <p className="truncate text-2xs text-content-subtle">
                {mimeType}
                {sizeBytes !== undefined && ` · ${formatFileSize(sizeBytes)}`}
              </p>
            </div>
          </div>

          {showZoom && (
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                iconLeft="minus"
                iconOnly
                aria-label="Zoom out"
                disabled={!fit && zoomIndex === 0}
                onClick={() => {
                  zoomBy(-1);
                }}
              />
              <span className="min-w-11 text-center text-2xs text-content-muted tabular-nums">
                {fit ? 'Fit' : `${String(Math.round(zoom * 100))}%`}
              </span>
              <Button
                variant="secondary"
                size="sm"
                iconLeft="plus"
                iconOnly
                aria-label="Zoom in"
                disabled={!fit && zoomIndex === ZOOM_STEPS.length - 1}
                onClick={() => {
                  zoomBy(1);
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFit(true);
                  setZoomIndex(DEFAULT_ZOOM_INDEX);
                }}
              >
                Reset
              </Button>
            </div>
          )}

          {onDownload && (
            <Button
              variant="secondary"
              size="sm"
              iconLeft="download"
              className="ml-auto sm:ml-0"
              onClick={onDownload}
            >
              Download
            </Button>
          )}

          <Button
            variant="secondary"
            size="sm"
            iconLeft="close"
            iconOnly
            aria-label="Close preview"
            onClick={onClose}
          />
        </header>

        {details.length > 0 && (
          <dl className="chrome-bar flex shrink-0 flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-border px-3 py-1.5">
            {details.map((detail) => (
              <div key={detail.label} className="flex items-baseline gap-1.5">
                <dt className="text-2xs tracking-wide text-content-subtle uppercase">
                  {detail.label}
                </dt>
                <dd
                  className={cn(
                    'text-xs font-medium',
                    detail.tone === 'danger' && 'text-danger',
                    detail.tone === 'warning' && 'text-warning',
                    (detail.tone ?? 'default') === 'default' && 'text-content',
                  )}
                >
                  {detail.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* Centring is done with auto margins rather than `justify-center`:
            a flex-centred child that outgrows its container overflows past the
            start edge, which no amount of scrolling can reach. */}
        <div
          className="flex flex-1 overflow-auto p-3"
          onClick={(event) => {
            /* The dark space around the document is a backdrop, and a
               backdrop closes - the same gesture as everywhere else in the
               app. Only when the click lands on this element itself: on the
               picture it is somebody looking, not dismissing. */
            if (event.target === event.currentTarget) {
              onClose();
            }
          }}
        >
          <Body
            kind={kind}
            loaded={loaded}
            title={title}
            mimeType={mimeType}
            width={fit ? null : naturalWidth * zoom}
            onImageLoad={setNaturalWidth}
            onDownload={onDownload}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Body({
  kind,
  loaded,
  title,
  mimeType,
  width,
  onImageLoad,
  onDownload,
}: {
  kind: 'image' | 'pdf' | undefined;
  loaded: Loaded | null;
  title: string;
  mimeType: string;
  /** Rendered width in pixels, or null to fit the window. */
  width: number | null;
  onImageLoad: (naturalWidth: number) => void;
  onDownload?: () => void;
}) {
  if (kind === undefined) {
    return (
      <Notice
        icon="shield"
        title="This file cannot be previewed"
        onDownload={onDownload}
        detail={mimeType}
      >
        Only PDFs and images are shown in the app. Other formats are downloaded so your own software
        opens them.
      </Notice>
    );
  }

  if (loaded === null) {
    return (
      <div className="m-auto flex flex-col items-center gap-2 text-white">
        <Spinner size={28} label="Loading document" />
        <p className="text-xs">Loading…</p>
      </div>
    );
  }

  if (loaded.status === 'failed') {
    return (
      <Notice
        icon="info"
        title="Could not open this document"
        tone="danger"
        onDownload={onDownload}
      >
        {loaded.message}
      </Notice>
    );
  }

  if (kind === 'image') {
    return (
      <img
        src={loaded.url}
        alt={title}
        onLoad={(event) => {
          onImageLoad(event.currentTarget.naturalWidth);
        }}
        style={width === null ? undefined : { width }}
        className={
          width === null
            ? 'm-auto max-h-full max-w-full bg-white object-contain shadow-lg'
            : 'm-auto max-w-none bg-white shadow-lg'
        }
      />
    );
  }

  /*
   * The PDF is handed to the browser's own viewer, deliberately without a
   * `sandbox` attribute.
   *
   * That looks like the wrong call and is worth spelling out: Chromium refuses
   * to instantiate its PDF viewer inside *any* sandboxed frame - tested here
   * with `sandbox=""`, `allow-scripts`, and `allow-scripts allow-same-origin`,
   * all of which render a broken-file icon rather than the document. The
   * sandbox attribute is not available, so the safety has to come from
   * elsewhere, and it does:
   *
   *  - The blob is typed `application/pdf` from RENDERABLE_TYPES, so these
   *    bytes reach the PDF parser and can never reach the HTML parser. A file
   *    that is secretly markup is inert here, and the API's signature check
   *    means it could not have been stored as a PDF in the first place.
   *  - The viewer itself runs out of process in the browser's own renderer
   *    sandbox, which is a great deal stronger than an iframe attribute, and
   *    it restricts the JavaScript a PDF is allowed to carry.
   */
  return (
    <iframe
      src={loaded.url}
      title={title}
      referrerPolicy="no-referrer"
      className="h-full w-full flex-1 border-0 bg-white"
    />
  );
}

function Notice({
  icon,
  title,
  tone = 'neutral',
  detail,
  children,
  onDownload,
}: {
  icon: 'shield' | 'info';
  title: string;
  tone?: 'neutral' | 'danger';
  detail?: string;
  children: React.ReactNode;
  onDownload?: () => void;
}) {
  return (
    <div
      className={
        tone === 'danger'
          ? 'm-auto max-w-sm rounded-sm border border-danger/30 bg-surface p-5 text-center'
          : 'm-auto max-w-sm rounded-sm border border-border bg-surface p-5 text-center'
      }
    >
      <Icon
        name={icon}
        size={22}
        className={tone === 'danger' ? 'mx-auto text-danger' : 'mx-auto text-content-muted'}
      />
      <p className="mt-2 text-sm font-semibold text-content">{title}</p>
      <p className="mt-1 text-xs text-content-muted">{children}</p>
      {detail !== undefined && (
        <p className="mt-2 font-mono text-2xs text-content-subtle">{detail}</p>
      )}
      {onDownload && (
        <Button
          variant="primary"
          size="md"
          iconLeft="download"
          className="mt-3"
          onClick={onDownload}
        >
          Download instead
        </Button>
      )}
    </div>
  );
}
